import React, { useState } from "react";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ReactAsyncSelect } from "@services/ui-components";
import { CitySelectFieldsBaseProps, Option } from "../../../interfaces/interfaces";
import { CUSTOM_CITY_OPTION_VALUE } from "../../../constants/city";
import { getCitySelectValue } from "@me-team/common/src/components/CitySelect/helpers/getCitySelectValue";

interface CompanyCityFormValues {
    country: string | number | null;
    city: number | null;
    customCity?: string | null;
}

interface CompanyCitySelectFieldsProps extends CitySelectFieldsBaseProps {
    cityRef?: React.RefObject<HTMLDivElement>;
    disabled?: boolean;
}

const isCustomCityOption = (option: Option | null) => {
    return option ? Number(option.value) === CUSTOM_CITY_OPTION_VALUE : false;
};

const CompanyCitySelectFields: React.FC<CompanyCitySelectFieldsProps> = ({
    cityFieldId,
    customCityFieldId,
    cityRef,
    cityOptions,
    selectedCityOption,
    showCustomCityInput,
    setShowCustomCityInput,
    isLoading,
    disabled = false,
    onMenuOpen,
    onMenuClose,
    onInputChange,
    onMenuScrollToBottom,
}) => {
    const { t } = useTranslation();
    const { values, errors, touched, setFieldValue } = useFormikContext<CompanyCityFormValues>();
    const [currentSelectedCityOption, setCurrentSelectedCityOption] = useState<Option | null>(null);

    const handleCityChange = (selectedOption: Option | null) => {
        if (isCustomCityOption(selectedOption)) {
            setShowCustomCityInput(true);
            setFieldValue("city", null);
            setCurrentSelectedCityOption(null);
            return;
        }

        setShowCustomCityInput(false);
        setFieldValue("city", selectedOption ? selectedOption.value : null);
        setFieldValue("customCity", null);
        setCurrentSelectedCityOption(selectedOption);
    };

    const handleCustomCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue("customCity", event.target.value);
    };

    return (
        <>
            <Row className="mb-3 mb-sm-4">
                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                    <label htmlFor={cityFieldId} className="required-label fs-7 fw-bold mt-sm-2 pt-1">
                        {t("city")}
                    </label>
                </Col>
                <Col sm={9} xxl={10} ref={cityRef}>
                    <ReactAsyncSelect
                        id={cityFieldId}
                        name="city"
                        inputAutoComplete="new-password"
                        key={`city-${values.country}`}
                        options={cityOptions}
                        value={getCitySelectValue({
                            showCustomCityInput,
                            cityOptions,
                            cityValue: values.city,
                            currentSelectedCityOption,
                            selectedCityOption,
                        })}
                        onChange={handleCityChange}
                        placeholder={t("Select City")}
                        isSearchable
                        isLoading={isLoading}
                        onMenuOpen={onMenuOpen}
                        onMenuClose={onMenuClose}
                        onInputChange={onInputChange}
                        onMenuScrollToBottom={onMenuScrollToBottom}
                        disableLocalFilter
                        noOptionsMessage={() => t("No options")}
                        loadingMessage={() => t("Loading...")}
                        disabled={disabled}
                    />
                    <ErrorMessage name="city">
                        {(message) => <p className="fs-7 p-1 text-danger m-0">{message}</p>}
                    </ErrorMessage>
                </Col>
            </Row>

            {showCustomCityInput && (
                <Row className="mb-3 mb-sm-4">
                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                        <label htmlFor={customCityFieldId} className="required-label fs-7 fw-bold mt-sm-2 pt-1">
                            {t("Add custom city name")}
                        </label>
                    </Col>
                    <Col sm={9} xxl={10}>
                        <Field
                            id={customCityFieldId}
                            name="customCity"
                            autoComplete="new-password"
                            placeholder={t("Enter custom city name")}
                            className={`form-control ${errors.customCity && touched.customCity ? "border-danger" : "border-secondary"}`}
                            onChange={handleCustomCityChange}
                            maxLength={50}
                        />
                        <ErrorMessage name="customCity">
                            {(message) => <p className="fs-7 p-1 text-danger m-0">{message}</p>}
                        </ErrorMessage>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default CompanyCitySelectFields;
